/**
 * useLumosityAuth.js — Clerk session state + token minting for lumosity_infra.
 *
 * Separate from useAuth.js (Google Identity Services), which authorizes this
 * app's own backend and Gmail/Sheets flows. Clerk auth exists ONLY to call
 * lumosity_infra endpoints and lives alongside it.
 *
 * IMPORTANT: only call this hook from components rendered when
 * LUMOSITY_ENABLED is true — Clerk's hooks throw outside <ClerkProvider>,
 * and main.jsx mounts the provider only when the publishable key is set.
 *
 * In production, sales.implied.com shares the Clerk instance's root domain
 * (clerk.implied.com), so anyone already signed into app.implied.com is
 * signed in here automatically — signInToLumosity() is the fallback.
 */

import { useCallback } from 'react';
import { useAuth as useClerkAuth, useClerk } from '@clerk/clerk-react';

// JWT template that adds the email/org_role/org_name claims lumosity_infra's
// watchlist route requires. Same template the Implied web app uses.
const LUMOSITY_JWT_TEMPLATE = 'implied_backend';

export function useLumosityAuth() {
  const { isLoaded, isSignedIn, getToken } = useClerkAuth();
  const clerk = useClerk();

  // Mints a fresh short-lived session JWT; Clerk caches/refreshes internally.
  const getClerkToken = useCallback(
    () => getToken({ template: LUMOSITY_JWT_TEMPLATE }),
    [getToken]
  );

  const signInToLumosity = useCallback(() => clerk.openSignIn({}), [clerk]);

  return {
    clerkLoaded: isLoaded,
    clerkSignedIn: isLoaded && isSignedIn,
    getClerkToken,
    signInToLumosity,
  };
}
